// pages/courses-overview/TenancyCourseEdit.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Button, Typography, Alert, Snackbar, CircularProgress } from '@mui/material';
import KeyboardArrowLeftTwoToneIcon from '@mui/icons-material/KeyboardArrowLeftTwoTone';
import MainCard from '../../components/cards/MainCard';
import TenancyCourseForm from './TenancyCourseForm';
import CourseProgressIndicator from '../../components/Loading/CourseProgressIndicator';
import { useCourseCreationFlow } from './useCourseCreationFlow';
import { useGetCourseByIdQuery } from '../../store/services/university-service';
import { getCourseMediaUrls } from '../../utils/coursePathUtils';


export default function TenancyCourseEdit() {
    const { id } = useParams();
    const navigate = useNavigate();

    const {
        state,
        error,
        executeCreationFlow,
        reset,
        isProcessing,
        isComplete,
        isError,
    } = useCourseCreationFlow('edit');

    const {
        data: courseData,
        isLoading: courseLoading,
        error: courseError,
    } = useGetCourseByIdQuery({
        courseId: id,
        includes: '',
    });

    const [formData, setFormData] = useState({
        courseName: '',
        languageCodes: [],
        published: false,
        sharedTenancyIds: [],
        publishToFamily: false,
        scoredOutOf: 100,
        format: 'SCORM',
        isLms: false,
        tag: '',
    });

    const [pendingFiles, setPendingFiles] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [mediaTimestamp, setMediaTimestamp] = useState(Date.now());

    // Load course data into form
    useEffect(() => {
        if (courseData) {
            setFormData({
                courseName: courseData.courseName || '',
                languageCodes: courseData.languageCodes
                    ? Array.isArray(courseData.languageCodes)
                        ? courseData.languageCodes
                        : [courseData.languageCodes]
                    : courseData.languageCode
                        ? [courseData.languageCode]
                        : [],
                published: courseData.published || false,
                sharedTenancyIds: courseData.sharedTenancyIds || [],
                publishToFamily: courseData.publishToFamily || false,
                scoredOutOf: courseData.scoredOutOf || 100,
                format: courseData.format || 'SCORM',
                linkedTypeformCourse: courseData.linkedTypeformCourse || '',
                isLms: courseData.isLms,
                tag: courseData.tag || ''
            });
        }
    }, [courseData]);

    useEffect(() => {
        if (isComplete) {
            setSnackbar({
                open: true,
                message: 'Course updated successfully!',
                severity: 'success',
            });

            // Clear pending files to enable preview before redirect
            setPendingFiles({});

            // Refresh media timestamp to show updated images
            setMediaTimestamp(Date.now());
        }
    }, [isComplete, navigate]);

    const handleSubmit = async () => {
        await executeCreationFlow(formData, pendingFiles, id);
    };

    const handleRetry = () => {
        reset();
    };

    const handleBack = () => {
        if (isProcessing) {
            const confirmLeave = window.confirm(
                'Course update is in progress. Are you sure you want to leave? This may cause the upload to fail.'
            );
            if (!confirmLeave) return;
        }
        navigate("/tenancy-courses");
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };


    const { imageUrl: existingImageUrl, gifUrl: existingGifUrl } = getCourseMediaUrls(
        courseData?.courseContentPath,
        formData.isLms,
        mediaTimestamp
    );


    const existingCourseContentPath = courseData?.courseContentPath || null;

    if (courseLoading) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '400px' }}>
                <CircularProgress />
            </Grid>
        );
    }

    if (courseError) {
        return (
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Alert severity="error">
                        Error loading course: {courseError.message || 'Unknown error'}
                    </Alert>
                    <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Button
                        onClick={handleBack}
                        variant="contained"
                        startIcon={<KeyboardArrowLeftTwoToneIcon />}
                        disabled={isProcessing}
                    >
                        Back
                    </Button>
                </Grid>

                <Grid item xs={12} lg={12}>

                    <Typography variant="h2" mb={3}>
                        {courseData?.courseName}
                    </Typography>

                    {!isProcessing && !isError && (
                        <TenancyCourseForm
                            formData={formData}
                            onChange={setFormData}
                            pendingFiles={pendingFiles}
                            onFileChange={setPendingFiles}
                            onSubmit={handleSubmit}
                            isProcessing={isProcessing}
                            mode="edit"
                            existingImageUrl={existingImageUrl}
                            existingGifUrl={existingGifUrl}
                            existingCourseContentPath={existingCourseContentPath}
                            courseId={id}
                        />
                    )}

                    {(isProcessing || isError) && (
                        <CourseProgressIndicator
                            state={state}
                            error={error}
                            onRetry={handleRetry}
                            mode="edit"
                        />
                    )}
                </Grid>

            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}